import pacote from '../../package.json';
import { FORCE_CACHE_RESET_AT_VERSION } from '@/constants/forceCacheReset';
import { deleteAppIndexedDatabasesCompletely } from '@/talons/dbFunctions/useDBFunctions';
import { unregisterServiceWorker } from '@/shared/serviceWorkFunctions';
import { migrateLegacyCartDetails } from '@/shared/cartStore';
import {
  LEGACY_DATASETS_DB,
  LEGACY_IMAGES_DB,
  dropLegacyDb,
  markLegacyHeavyMigrationDone,
} from '@/shared/legacyIdb';
import { getLocalStorageKeyForTable, type Tables } from '@/shared/storageFunctions';

const MARKER_KEY = 'pwa_last_force_cache_reset_version';

/** Chaves do carrinho e correlatas no storage tipado — não limpas no reset forçado. */
const CART_KEYS_TO_PRESERVE = [
  'cart_id',
  'cart_details',
  'cart_sort',
  'order_numbers',
  'client_cart',
  'cart',
  'selected_brand',
] as const satisfies readonly Tables[];

function compareSemver(a: string, b: string): number {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const da = pa[i] ?? 0;
    const db = pb[i] ?? 0;
    if (da !== db) return da > db ? 1 : -1;
  }
  return 0;
}

function snapshotCartRawEntries(): [string, string][] {
  const out: [string, string][] = [];
  for (const name of CART_KEYS_TO_PRESERVE) {
    const key = getLocalStorageKeyForTable(name);
    const value = localStorage.getItem(key);
    if (value !== null) out.push([key, value]);
  }
  return out;
}

function restoreRawEntries(entries: [string, string][]) {
  for (const [k, v] of entries) localStorage.setItem(k, v);
}

/**
 * Se a versão do app e a constante `FORCE_CACHE_RESET_AT_VERSION` exigirem, apaga IndexedDB
 * (dados + imagens), zera `localStorage` exceto entradas do carrinho, limpa `sessionStorage`,
 * desregistra o service worker e retorna `true` para o caller dar um `reload` antes do primeiro paint.
 */
export async function maybeApplyForcedCacheReset(): Promise<boolean> {
  const target = FORCE_CACHE_RESET_AT_VERSION.trim();
  if (!target) return false;

  const current = (pacote.version || '').trim();
  if (!current || compareSemver(current, target) < 0) return false;

  const last = localStorage.getItem(MARKER_KEY);
  if (last === target) return false;

  // Migra cart_details (localStorage / blob legado → IndexedDB BF_SELLER_CARTS)
  // ANTES de limpar o localStorage, para quem ainda está na versão antiga do app.
  try {
    await migrateLegacyCartDetails();
  } catch (e) {
    console.warn('[forceCacheReset] Aviso ao migrar carrinho legado (seguindo mesmo assim):', e);
  }

  const cartSnapshot = snapshotCartRawEntries();

  // SW e Cache API primeiro: evita bundle antigo/cache competindo com IDB novo no PWA.
  try {
    await unregisterServiceWorker({
      preserveLocalStorage: true,
      setReloadFlag: false,
    });
  } catch (e) {
    console.warn('[forceCacheReset] Aviso ao desregistrar SW (seguindo mesmo assim):', e);
  }

  await new Promise<void>((r) => setTimeout(r, 250));

  let idbOk = await deleteAppIndexedDatabasesCompletely();
  if (!idbOk) {
    console.warn('[forceCacheReset] Primeira exclusão do IDB incompleta; nova tentativa em 1,5s…');
    await new Promise<void>((r) => setTimeout(r, 1500));
    idbOk = await deleteAppIndexedDatabasesCompletely();
  }
  if (!idbOk) {
    console.error('[forceCacheReset] IndexedDB não foi limpo com segurança; marcador NÃO gravado — tentará de novo no próximo carregamento.');
    return false;
  }

  // Os bancos pré-renomeação de datasets e imagens também precisam sair:
  // `deleteAppIndexedDatabasesCompletely` só conhece a família `BF_SELLER_*`, e deixar centenas de
  // MB de `BATE_FORTE` para trás é justamente a pressão de disco que este reset foi publicado para
  // aliviar. É conteúdo de servidor — o sync o reescreve no formato novo.
  //
  // Os bancos legados de carrinho e de pedido ficam de fora **de propósito**. Este reset preserva o
  // carrinho (ver `CART_KEYS_TO_PRESERVE`), e se a fase crítica tiver falhado na leitura, os dois
  // bancos antigos são o único lugar onde aquele pedido offline ainda existe: apagá-los aqui
  // transformaria uma migração que ia se repetir no próximo boot em perda definitiva.
  for (const name of [LEGACY_DATASETS_DB, LEGACY_IMAGES_DB]) {
    await dropLegacyDb(name);
  }

  try {
    localStorage.clear();
    restoreRawEntries(cartSnapshot);
    localStorage.setItem(MARKER_KEY, target);

    // Depois do `clear()`, senão os marcadores iriam embora com o resto. Sem eles a fase pesada
    // copiaria as imagens antigas de volta no próximo boot, desfazendo o wipe que acabou de rodar.
    markLegacyHeavyMigrationDone();

    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.clear();
    }
  } catch (e) {
    console.error('[forceCacheReset] Falha ao limpar/restaurar localStorage:', e);
    return false;
  }

  return true;
}
